import { Observable, fromEvent, fromEventPattern, shareReplay } from "rxjs";
import { NodeEventHandler } from "rxjs/internal/observable/fromEvent";

declare const document: Document & {
  attachEvent: any;
  createEventObject: any;
  onmousewheel: any;
};

export type EventMap = GlobalEventHandlersEventMap &
  ElementEventMap &
  HTMLElementEventMap &
  DocumentEventMap &
  WindowEventHandlersEventMap &
  WindowEventMap &
  MediaQueryListEventMap;

// event.type == 'mouseover'
type EventName =
  | keyof EventMap
  | "wheel"
  | "mousewheel"
  | "DOMMouseScroll"
  | "transitionend"
  | "webkitTransitionEnd"
  | "oTransitionEnd"
  | "MSTransitionEnd";

type EventScope =
  | MouseEvent
  | KeyboardEvent
  | TouchEvent
  | PointerEvent
  | DragEvent
  | Event
  | CustomEvent
  | UIEvent
  | TransitionEvent
  | AnimationEvent
  | StorageEvent
  | any;

// event.target: Refers to the actual element where the event occurred
// event.currentTarget: Refers to the element to which the event handler is attached

export function getEventCurrentTarget(e: any) {
  // first, IE method for mouse events(also supported by Safari and Opera)
  if (e.toElement) {
    return e.toElement;
    // W3C
  } else if (e.currentTarget) {
    return e.currentTarget;
    // MS way
  } else if (e.srcElement) {
    return e.srcElement;
  } else {
    return null;
  }
}

export type HTMLEventObservable =
  | EventTarget
  | HTMLElement
  | SVGElement
  | SVGSVGElement
  | HTMLCanvasElement
  | any;

export function useEventListener(
  target: HTMLEventObservable,
  eventName: EventName,
  handler: (e: EventScope) => void | Promise<void>,
  useCapture = false // [ 捕获阶段 -> 目标阶段 -> 冒泡阶段 ] useCapture 用于指定是否在捕获阶段进行处理，一般设置为 false 与 IE浏览器保持一致
): () => void {
  const eventHandler = async (e: EventScope) => {
    await handler(e);
  };

  try {
    target.addEventListener(eventName, eventHandler, useCapture);
  } catch (e) {
    target.attachEvent("on" + eventName, eventHandler);
  }

  return () => {
    try {
      target.removeEventListener(eventName, eventHandler, useCapture);
    } catch (e) {
      target.detachEvent("on" + eventName, eventHandler);
    }
  };
}

// 绑定事件监听器到父元素
export function useParentEventListener(
  parent: HTMLEventObservable,
  target: HTMLEventObservable,
  eventName: EventName,
  handler: (e: EventScope) => void | Promise<void>,
  useCapture = false // [ 捕获阶段 -> 目标阶段 -> 冒泡阶段 ] useCapture 用于指定是否在捕获阶段进行处理，一般设置为 false 与 IE浏览器保持一致
): () => void {
  return useEventListener(
    parent || document.body,
    eventName,
    (e) => {
      if (e.target === target) {
        // hit 目标元素!
        handler(e);
      }
    },
    useCapture
  );
}

export function removeAllEventListeners(target: HTMLEventObservable) {
  target.replaceWith(target.cloneNode(true));
}

export function useEvent$<T extends EventScope = EventScope>(
  target: HTMLEventObservable,
  eventName: EventName,
  useCapture = false // [ 捕获阶段 -> 目标阶段 -> 冒泡阶段 ] useCapture 用于指定是否在捕获阶段进行处理，一般设置为 false 与 IE浏览器保持一致
): Observable<T> {
  // 当取消订阅时，fromEvent 会自动停止监听事件
  return fromEvent<T>(target, eventName, { capture: useCapture });
}

export function createFromEventPattern(options: {
  register: (handler: NodeEventHandler) => any;
  unregister?: (handler: NodeEventHandler, ...args: any[]) => any;
  share?: boolean;
  bufferSize?: number;
}): Observable<any[]> {
  // observer.next 默认只会接收「自定义事件机制」emit 的第一个参数
  const $ = fromEventPattern(
    (handler: NodeEventHandler) => {
      return options.register(handler);
    },
    (handler: NodeEventHandler, args?: any[]) => {
      options.unregister?.(handler, ...(args || []));
    },
    // 参数转换函数
    (...args: any[]) => {
      return [...args];
    }
  );
  if (options.share) {
    return $.pipe(
      shareReplay({
        bufferSize: options.bufferSize || 1, // 每个新订阅会收到最近一次事件
        refCount: true // 最后一个订阅取消时会自动解绑事件
      })
    );
  } else {
    return $;
  }
}

// e.preventDefault();
export function preventDefault(e: EventScope) {
  if (e.preventDefault) {
    e.preventDefault();
    return;
  }
  try {
    e.returnValue = false;
  } catch (ex) {
    // do nothing
  }
}

// e.stopPropagation();
// e.stopImmediatePropagation();
export function stopPropagation(e: EventScope) {
  if (e.stopPropagation) {
    e.stopPropagation();
    return;
  }
  try {
    e.cancelBubble = true;
  } catch (ex) {
    // do nothing
  }
}

// https://dom.spec.whatwg.org/#dom-document-createevent
// 以下按需扩展
const MouseEvents = [
  "click",
  "dblclick",
  "wheel",
  "contextmenu",
  "mousedown",
  "mouseup",
  "mousemove",
  "mouseover",
  "mouseout",
  "mouseenter",
  "mouseleave"
];
const KeyboardEvents = ["keydown", "keyup", "keypress"];
const TouchEvents = ["touchstart", "touchend", "touchcancel", "touchmove"];
const PointerEvents = [
  "pointerdown",
  "pointerup",
  "pointercancel",
  "pointermove"
];
const DragEvents = [
  "dragstart",
  "drag",
  "dragend",
  "dragenter",
  "dragover",
  "dragleave",
  "drop"
];
const Events = ["change"];

export function createEvent(
  eventName: EventName, // click keydown ...
  options:
    | MouseEventInit
    | KeyboardEventInit
    | TouchEventInit
    | PointerEventInit
    | DragEventInit
    | EventInit
    | CustomEventInit = {
    bubbles: true,
    detail: {}
  }
): EventScope {
  let event;
  try {
    const o = Object.assign(
      {
        bubbles: true,
        cancelable: true,
        view: window,
        detail: 1,
        // screenX: e.screenX,
        // screenY: e.screenY,
        // clientX: e.clientX,
        // clientY: e.clientY,
        ctrlKey: false,
        altKey: false,
        shiftKey: false,
        metaKey: false,
        button: 0,
        relatedTarget: null
      },
      options
    );
    if (MouseEvents.includes(eventName)) {
      event = new MouseEvent(eventName, o);
    } else if (KeyboardEvents.includes(eventName)) {
      event = new KeyboardEvent(eventName, o);
    } else if (TouchEvents.includes(eventName)) {
      event = new TouchEvent(eventName, o);
    } else if (PointerEvents.includes(eventName)) {
      event = new PointerEvent(eventName, o);
    } else if (DragEvents.includes(eventName)) {
      event = new DragEvent(eventName, o);
    } else if (Events.includes(eventName)) {
      event = new Event(eventName, o);
    } else {
      // const event = new CustomEvent("awesome", { detail: { text: () => textarea.value } });
      // el.addEventListener("awesome", (e) => console.log(e.detail.text()));
      event = new CustomEvent(eventName, o);
    }
  } catch (e) {
    // fallback
    if (document.createEvent) {
      // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/initMouseEvent
      // initMouseEvent is deprecated.
      event = document
        // .createEvent("HTMLEvents")
        .createEvent("Event")
        .initEvent(eventName, true, true); // event-type, bubbling, cancelable
    } else if (document.createEventObject) {
      event = document.createEventObject();
    }
  }
  return event;
}

export function dispatchEvent(
  target: HTMLEventObservable,
  eventName: EventName, // click keydown ...
  event: EventScope
) {
  if (!target) {
    return;
  }
  if (target.dispatchEvent) {
    // dispatch for firefox + ie9 + others
    target.dispatchEvent(event);
  } else if (target.fireEvent && document.createEventObject) {
    // dispatch for IE8 and lower
    target.fireEvent("on" + eventName, event);
  } else {
    throw new Error(`Can't dispatch event : ${eventName}`);
  }
}

export function dispatchEventFromEvent(e: EventScope, eventName: EventName) {
  const target = e.target;
  const event = createEvent(eventName);
  dispatchEvent(target, eventName, event);
}

export function dispatchClickFromEvent(e: EventScope) {
  const target = e.target as any;
  const eventName = "click";
  if (!/(SELECT|INPUT|TEXTAREA)/i.test(target.tagName)) {
    const event = createEvent(eventName) as any;
    event.view = (e as any).view || window;
    event.detail = 1;
    event.screenX = target.screenX || 0;
    event.screenY = target.screenY || 0;
    event.clientX = target.clientX || 0;
    event.clientY = target.clientY || 0;
    event.pageX = (e as any).pageX;
    event.pageY = (e as any).pageY;
    event.ctrlKey = !!(e as any).ctrlKey;
    event.altKey = !!(e as any).altKey;
    event.shiftKey = !!(e as any).shiftKey;
    event.metaKey = !!(e as any).metaKey;
    event.button = 0;
    event.relatedTarget = null;
    event._constructed = true;
    dispatchEvent(target, eventName, event);
  }
}

export function simulateEventFromTouch(
  touchEvent: TouchEvent,
  eventName: EventName
): EventScope {
  const touch = touchEvent.changedTouches[0];
  return createEvent(eventName, {
    bubbles: true,
    cancelable: true,
    view: window,
    detail: 1,
    screenX: touch.screenX,
    screenY: touch.screenY,
    clientX: touch.clientX,
    clientY: touch.clientY,
    ctrlKey: false,
    altKey: false,
    shiftKey: false,
    metaKey: false,
    button: 0,
    relatedTarget: null
  });
}
